import { formatMoneyKzt } from "@/shared/utils";
import type { Locale } from "@/shared/utils";
import type { AuditEntry } from "@/shared/types/audit";

interface IncomeSourceEntry {
  income_id: number;
  income_name?: string;
  amount: string;
}

interface RedistributeMetadata {
  source_kind?: "project" | "external_pool";
  source_project_id?: number;
  income_sources?: IncomeSourceEntry[];
  income_sources_transferred?: IncomeSourceEntry[];
  source_deltas?: Record<string, string>;
  target_deltas?: Record<string, string>;
}

interface IncomeSourceMetadata {
  income_id: number;
  income_name?: string;
  amount?: string;
  returned_amount?: string;
  pool?: string;
}

function resolveIncomeName(
  source: IncomeSourceEntry | IncomeSourceMetadata,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  if (source.income_name) return source.income_name;
  return t("history.redistribute.incomeFallback", { id: source.income_id });
}

function renderDeltaRows(
  deltas: Record<string, string>,
  sign: "negative" | "positive",
  t: (key: string, params?: Record<string, unknown>) => string,
  locale: Locale,
) {
  return Object.entries(deltas).map(function renderDelta([category, amount]) {
    const categoryKey = `history.categories.${category}`;
    const categoryLabel = t(categoryKey);
    const label = categoryLabel === categoryKey ? category : categoryLabel;
    return (
      <div key={category} className="flex items-center justify-between gap-2">
        <span className="text-body-regular-xs content-base-secondary">{label}</span>
        <span
          className={
            sign === "negative" ? "text-body-bold-xs content-action-negative" : "text-body-bold-xs content-action-positive"
          }>
          {sign === "negative" ? "-" : "+"}
          {formatMoneyKzt(amount, locale)}
        </span>
      </div>
    );
  });
}

export function renderRedistributedMetadata(
  metadata: RedistributeMetadata | null | undefined,
  t: (key: string, params?: Record<string, unknown>) => string,
  locale: Locale,
): React.ReactNode | null {
  if (!metadata) return null;

  const incomeSources =
    metadata.source_kind === "project" ? metadata.income_sources_transferred : metadata.income_sources;
  const hasSourceDeltas = !!metadata.source_deltas && Object.keys(metadata.source_deltas).length > 0;
  const hasTargetDeltas = !!metadata.target_deltas && Object.keys(metadata.target_deltas).length > 0;
  const hasIncomeSources = !!incomeSources && incomeSources.length > 0;

  if (!hasSourceDeltas && !hasTargetDeltas && !hasIncomeSources && metadata.source_kind === undefined) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col gap-2 p-3 radius-sm surface-secondary-fill">
      {metadata.source_kind === "project" && metadata.source_project_id !== undefined && (
        <p className="text-body-regular-xs content-base-secondary">
          {t("history.redistribute.fromProject")}:{" "}
          <span className="text-body-bold-xs content-base-primary">
            {t("history.redistribute.projectRef", { id: metadata.source_project_id })}
          </span>
        </p>
      )}
      {metadata.source_kind === "external_pool" && (
        <p className="text-body-regular-xs content-base-secondary">{t("history.redistribute.fromExternalPool")}</p>
      )}

      {hasIncomeSources && (
        <div className="flex flex-col gap-1">
          <p className="text-label-2xs content-action-neutral">{t("history.redistribute.incomeSources")}:</p>
          {incomeSources!.map(function renderIncome(source) {
            return (
              <div key={source.income_id} className="flex items-center justify-between gap-2">
                <span className="text-body-regular-xs content-base-secondary">{resolveIncomeName(source, t)}</span>
                <span className="text-body-bold-xs content-base-primary">{formatMoneyKzt(source.amount, locale)}</span>
              </div>
            );
          })}
        </div>
      )}

      {hasSourceDeltas && (
        <div className="flex flex-col gap-1">
          <p className="text-label-2xs content-action-neutral">{t("history.redistribute.sourceDeltasTitle")}:</p>
          {renderDeltaRows(metadata.source_deltas!, "negative", t, locale)}
        </div>
      )}

      {hasTargetDeltas && (
        <div className="flex flex-col gap-1">
          <p className="text-label-2xs content-action-neutral">{t("history.redistribute.targetDeltasTitle")}:</p>
          {renderDeltaRows(metadata.target_deltas!, "positive", t, locale)}
        </div>
      )}
    </div>
  );
}

export function renderIncomeSourceMetadata(
  metadata: IncomeSourceMetadata | null | undefined,
  isReturned: boolean,
  t: (key: string, params?: Record<string, unknown>) => string,
  locale: Locale,
): React.ReactNode | null {
  if (!metadata) return null;

  const amount = isReturned ? metadata.returned_amount : metadata.amount;

  return (
    <div className="mt-2 flex flex-col gap-1 p-3 radius-sm surface-secondary-fill">
      <div className="flex items-center justify-between gap-2">
        <span className="text-body-regular-xs content-base-secondary">{resolveIncomeName(metadata, t)}</span>
        {amount !== undefined && (
          <span className="text-body-bold-xs content-base-primary">{formatMoneyKzt(amount, locale)}</span>
        )}
      </div>
      {metadata.pool && (
        <p className="text-label-2xs content-action-neutral">
          {t("history.redistribute.pool")}: {t(`history.redistribute.poolValue.${metadata.pool}`)}
        </p>
      )}
    </div>
  );
}

export function renderBudgetAuditMetadata(
  entry: AuditEntry,
  t: (key: string, params?: Record<string, unknown>) => string,
  locale: Locale,
): React.ReactNode | null {
  if (entry.action === "project.redistributed") {
    return renderRedistributedMetadata(entry.metadata as RedistributeMetadata | null, t, locale);
  }
  if (entry.action === "project.income_source_added") {
    return renderIncomeSourceMetadata(entry.metadata as IncomeSourceMetadata | null, false, t, locale);
  }
  if (entry.action === "project.income_source_returned") {
    return renderIncomeSourceMetadata(entry.metadata as IncomeSourceMetadata | null, true, t, locale);
  }
  return null;
}
